import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import debug from 'debug'
import { ID } from '../../utils/id'
import { AppDispatch, State } from '../index'
import { getParagraphIds } from '../transcript/selector'

const logger = debug('store/helper')

export type HelperState = {
  focused?: boolean
  paragraphId?: ID
  skippedWords: {
    [key: string]: number[]
  }
  startIndex?: number
  endIndex?: number
}

const initialState: HelperState = {
  endIndex: undefined,
  focused: false,
  paragraphId: undefined,
  skippedWords: {},
  startIndex: undefined,
}

export const helperSlice = createSlice({
  initialState,
  name: 'helper',
  reducers: {
    addSkipped: (
      state,
      action: PayloadAction<{
        paragraphId: string
        startIndex: number
        endIndex: number
      }>) => {
      if (!state.skippedWords[action.payload.paragraphId]) {
        state.skippedWords[action.payload.paragraphId] = []
      }
      for (let i = action.payload.startIndex; i <= action.payload.endIndex; i++) {
        if (!state.skippedWords[action.payload.paragraphId].includes(i)) {
          state.skippedWords[action.payload.paragraphId].push(i)
        }
      }
      return state
    },
    resetFocused: () => {
      return initialState
    },
    resetSkipped: (state) => {
      state.skippedWords = {}
      return state
    },
    setFocusedField: (state, action: PayloadAction<boolean>) => {
      state.focused = action.payload
      return state
    },
    setWords: (
      state,
      action: PayloadAction<{ startIndex?: number; endIndex?: number; paragraphId?: ID }>,
    ) => {
      state.startIndex = action.payload.startIndex
      state.endIndex = action.payload.endIndex
      state.paragraphId = action.payload.paragraphId
      return state
    },
  },
})

export const {
  resetFocused,
  resetSkipped,
  addSkipped,
  setFocusedField,
  setWords,
} = helperSlice.actions

export const setFocused = createAsyncThunk<void, boolean | undefined, {
  dispatch: AppDispatch
  state: State
}>(
  'helper/setFocused',
  async (focused, thunkAPI) => {
    if (!focused) {
      thunkAPI.dispatch(setFocusedField(false))
      return
    }

    const transcriptId = thunkAPI.getState().record.transcriptId

    await thunkAPI.dispatch(findNextWord({
      index: null,
      maxConfidence: 0.9,
      paragraphId: null,
      transcriptId,
    }))
    thunkAPI.dispatch(setFocusedField(focused))
  },
)


export const findNextWord = createAsyncThunk<boolean, {
  index: number | null
  paragraphId: ID | null
  transcriptId: ID | undefined
  maxConfidence: number
}, {
  dispatch: AppDispatch
  state: State
}>(
  'helper/findNextWord',
  async (data, thunkAPI): Promise<boolean> => {
    let maxConfidence = data.maxConfidence ?? 1
    const {paragraphId, transcriptId, index} = data

    logger('findNextWord %O', {
      index,
      maxConfidence,
      paragraphId,
      transcriptId,
    })

    const state = thunkAPI.getState()

    let paragraphFound = false
    let utteranceFound = false
    let done = false
    getParagraphIds(state, { transcriptId }).some((pid) => {
      if (!paragraphId || paragraphId === pid) {
        paragraphFound = true
      }

      if (!paragraphFound) {
        return false
      }
      done = false

      state
        .transcript
        .paragraphs[pid]
        .text
        .split(' ')
        .some((word, i) => {
          // Using isNaN does not work here as it returns false when we pass "null" as a paramater
          if (!Number.isFinite(index) || index === i) {
            utteranceFound = true
          }
          if (!utteranceFound) {
            return false
          }

          if (
            state.helper.skippedWords[pid]?.includes(i)
            || (state.transcript.paragraphs[pid]?.confidences?.[i] ?? 0) > maxConfidence
          ) {
            return false
          }
          thunkAPI.dispatch(setWords({
            endIndex: i,
            paragraphId: pid,
            startIndex: i,
          }))

          done = true
          return true
        })

      return done
    })

    if (!done && maxConfidence === 1) {
      thunkAPI.dispatch(setWords({
        endIndex: index ?? undefined,
        paragraphId: paragraphId ?? undefined,
        startIndex: index ?? undefined,
      }))
      return true
    } else if (!done) {
      if (maxConfidence <= 0.9) {
        maxConfidence = 0.95
      } else if (maxConfidence <= 0.95) {
        maxConfidence = 0.99
      } else if (maxConfidence <= 0.99) {
        if (paragraphId !== null && state.helper.skippedWords[paragraphId]?.length === 0) {
          return true
        }

        thunkAPI.dispatch(resetSkipped())
        maxConfidence = 0.9
      }

      const next = await thunkAPI.dispatch(findNextWord({
        index: null,
        maxConfidence: maxConfidence,
        paragraphId: null,
        transcriptId,
      }))
      return findNextWord.fulfilled.match(next) && next.payload
    }

    return true
  },
)
